import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Gavel, LogOut, ScrollText, History, LogIn, Sun, Shield, BookOpen,
  Users, UserRound, BookMarked, Menu, X, Book, Megaphone, Target, HeartHandshake, Rss, Crown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { mediaUrl } from "@/lib/api";

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile menu whenever the route changes
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const navItems = [
    { to: "/generate", label: "Generate", short: "Generate", Icon: ScrollText },
    { to: "/bible", label: "Bible", short: "Bible", Icon: Book },
    { to: "/plans", label: "Decree Plans", short: "Plans", Icon: Target },
    { to: "/daily", label: "Daily Decree", short: "Daily", Icon: Sun },
    { to: "/prayer", label: "Prayer Request", short: "Prayer", Icon: HeartHandshake },
    { to: "/feed", label: "Newsfeed", short: "Feed", Icon: Rss },
    { to: "/teachings", label: "Teachings", short: "Teachings", Icon: BookOpen },
    { to: "/lexicon", label: "Lexicon", short: "Lexicon", Icon: BookMarked },
    { to: "/community", label: "Community", short: "Community", Icon: Users },
    { to: "/history", label: "Your Decrees", short: "Decrees", Icon: History },
    { to: "/whats-new", label: "What's New", short: "Updates", Icon: Megaphone },
    ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", short: "Admin", Icon: Shield }] : []),
  ];

  const slug = (to) => to.replace("/", "");

  const isActive = (to) => location.pathname === to;

  const handleLogout = () => { logout(); setOpen(false); navigate("/"); };

  return (
    <>
    <header className="border-b border-[#D9D4CC] bg-[#FDFBF7]/80 backdrop-blur-sm sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 group">
          <Gavel className="w-5 h-5 text-[#2C2A29] transition-transform duration-300 group-hover:-rotate-12" strokeWidth={1.75} />
          <span className="font-heading text-lg sm:text-xl text-[#2C2A29] tracking-tight whitespace-nowrap">It Is Established</span>
        </Link>

        {user ? (
          <>
            {/* Desktop nav */}
            <nav className="hidden xl:flex items-center gap-1">
              {user.tier !== "premium" && (
                <Link
                  to="/premium"
                  data-testid="nav-premium"
                  className="flex items-center gap-1.5 px-3 py-1.5 mr-1 text-sm rounded-full border border-[#9B7E4E] text-[#9B7E4E] hover:bg-[#9B7E4E] hover:text-[#FAF9F6] transition-colors duration-300"
                >
                  <Crown className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span>Premium</span>
                </Link>
              )}
              {navItems.map(({ to, short, Icon }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-${slug(to)}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors duration-300 ${isActive(to) ? "text-[#2C2A29]" : "text-[#4A4744] hover:text-[#2C2A29]"}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                  <span>{short}</span>
                </Link>
              ))}
              <Link
                to="/profile"
                data-testid="nav-profile"
                aria-label="Profile"
                className="ml-1 w-8 h-8 rounded-full overflow-hidden border border-[#D9D4CC] bg-[#EAE6DF] flex items-center justify-center hover:border-[#B7AE9F] transition-colors duration-300"
              >
                {user.avatar_url ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" /> : <UserRound className="w-4 h-4 text-[#7A756E]" />}
              </Link>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#4A4744] hover:text-[#2C2A29] transition-colors duration-300"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.75} />
                <span>Sign out</span>
              </button>
            </nav>

            {/* Mobile: avatar + hamburger */}
            <div className="flex xl:hidden items-center gap-2">
              {user.tier !== "premium" && (
                <Link
                  to="/premium"
                  data-testid="nav-premium-mobile"
                  aria-label="Premium"
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-full border border-[#9B7E4E] text-[#9B7E4E]"
                >
                  <Crown className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span>Premium</span>
                </Link>
              )}
              <Link
                to="/profile"
                data-testid="nav-profile-mobile"
                aria-label="Profile"
                className="w-9 h-9 rounded-full overflow-hidden border border-[#D9D4CC] bg-[#EAE6DF] flex items-center justify-center"
              >
                {user.avatar_url ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" /> : <UserRound className="w-4 h-4 text-[#7A756E]" />}
              </Link>
              <button
                data-testid="mobile-menu-button"
                onClick={() => setOpen(true)}
                aria-label="Open menu"
                className="w-10 h-10 flex items-center justify-center rounded-sm text-[#2C2A29] hover:bg-[#EAE6DF] transition-colors duration-300"
              >
                <Menu className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <Link
              to="/bible"
              data-testid="nav-bible-guest"
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#4A4744] hover:text-[#2C2A29] transition-colors duration-300"
            >
              <Book className="w-4 h-4" strokeWidth={1.75} />
              <span>Bible</span>
            </Link>
            <Link
              to="/whats-new"
              data-testid="nav-whats-new-guest"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm text-[#4A4744] hover:text-[#2C2A29] transition-colors duration-300"
            >
              <Megaphone className="w-4 h-4" strokeWidth={1.75} />
              <span>Updates</span>
            </Link>
            <Link
              to="/prayer"
              data-testid="nav-prayer-guest"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm text-[#4A4744] hover:text-[#2C2A29] transition-colors duration-300"
            >
              <HeartHandshake className="w-4 h-4" strokeWidth={1.75} />
              <span>Prayer</span>
            </Link>
            <Link
              to="/premium"
              data-testid="nav-pricing-guest"
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#9B7E4E] hover:text-[#2C2A29] transition-colors duration-300"
            >
              <Crown className="w-4 h-4" strokeWidth={1.75} />
              <span>Pricing</span>
            </Link>
            <Link
              to="/auth"
              data-testid="nav-signin"
              className="flex items-center gap-1.5 px-4 py-2 text-sm border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300"
            >
              <LogIn className="w-4 h-4" strokeWidth={1.75} />
              <span>Sign in</span>
            </Link>
          </div>
        )}
      </div>
    </header>

      {/* Mobile drawer */}
      {user && (
        <div className={`xl:hidden fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
          <div
            data-testid="mobile-menu-overlay"
            onClick={() => setOpen(false)}
            className={`absolute inset-0 bg-[#2C2A29]/40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
          />
          <aside
            data-testid="mobile-menu-panel"
            className={`absolute top-0 right-0 h-full w-[80%] max-w-xs bg-[#FDFBF7] border-l border-[#D9D4CC] shadow-xl flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
          >
            <div className="h-16 px-5 flex items-center justify-between border-b border-[#D9D4CC]">
              <span className="font-heading text-lg text-[#2C2A29]">Menu</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="w-10 h-10 flex items-center justify-center rounded-sm text-[#2C2A29] hover:bg-[#EAE6DF] transition-colors duration-300"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>

            <Link to="/profile" className="flex items-center gap-3 px-5 py-4 border-b border-[#EAE6DF] hover:bg-[#F3F0EA] transition-colors duration-300">
              <span className="w-10 h-10 rounded-full overflow-hidden border border-[#D9D4CC] bg-[#EAE6DF] flex items-center justify-center shrink-0">
                {user.avatar_url ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" /> : <UserRound className="w-5 h-5 text-[#7A756E]" />}
              </span>
              <span className="min-w-0">
                <span className="block font-heading text-base text-[#2C2A29] truncate">{user.name || "Your profile"}</span>
                <span className="block font-body text-xs text-[#7A756E] truncate">{user.email}</span>
              </span>
            </Link>

            <nav className="flex-1 overflow-y-auto py-2">
              {user.tier !== "premium" && (
                <Link
                  to="/premium"
                  data-testid="mobile-nav-premium"
                  className="flex items-center gap-3 px-5 py-3.5 text-base text-[#9B7E4E] hover:bg-[#F3F0EA] transition-colors duration-300"
                >
                  <Crown className="w-5 h-5 shrink-0" strokeWidth={1.75} />
                  <span>Go Premium</span>
                </Link>
              )}
              {navItems.map(({ to, label, Icon }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`mobile-nav-${slug(to)}`}
                  className={`flex items-center gap-3 px-5 py-3.5 text-base transition-colors duration-300 ${isActive(to) ? "bg-[#EAE6DF] text-[#2C2A29]" : "text-[#4A4744] hover:bg-[#F3F0EA]"}`}
                >
                  <Icon className="w-5 h-5 shrink-0" strokeWidth={1.75} />
                  <span>{label}</span>
                </Link>
              ))}
            </nav>

            <button
              data-testid="mobile-logout-button"
              onClick={handleLogout}
              className="flex items-center gap-3 px-5 py-4 text-base text-[#4A4744] border-t border-[#D9D4CC] hover:bg-[#F3F0EA] transition-colors duration-300"
            >
              <LogOut className="w-5 h-5 shrink-0" strokeWidth={1.75} />
              <span>Sign out</span>
            </button>
          </aside>
        </div>
      )}
    </>
  );
}
